import type { MiddlewareAPI } from 'redux';
import 'rxjs/add/observable/of';
import 'rxjs/add/operator/mergeMap';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/switchMap';
import type { ActionsObservable } from 'redux-observable';
import { Observable } from 'rxjs/Observable';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import {
	fireTrackAnalytics,
	type ActionSubjectAndAction,
} from '@atlassian/jira-product-analytics-bridge';
import { AUTOMATION_RULE_ANALYTICS_SUBJECT } from '@atlassian/jira-servicedesk-automation-common/src/analytics';
import {
	addFlagAction,
	type AddFlagAction,
} from '@atlassian/jira-servicedesk-automation-common/src/state/flags/actions';
import type { Flag } from '@atlassian/jira-servicedesk-automation-common/src/state/flags/types';
import { ERROR_TYPE } from '@atlassian/jira-servicedesk-common/src/model/flags';
import { ofI18n } from '@atlassian/jira-servicedesk-common/src/ui/utils/displayable';
import { generateNanoId } from '@atlassian/jira-uuid';
import deleteRule from '../../../services/delete';
import {
	DELETE_RULE,
	type DeleteRuleAction,
	deleteSuccessAction,
	deleteFailureAction,
	setShowDeleteDialogAction,
} from '../../../state/actions/rule/delete';
import type { State } from '../../../state/types';
import messages from './messages';

const fireDeleteAnalytics = (deletedRuleSetId: string, analyticsEvent: UIAnalyticsEvent) => {
	const event: ActionSubjectAndAction = `${AUTOMATION_RULE_ANALYTICS_SUBJECT} deleted`;
	fireTrackAnalytics(analyticsEvent, event, deletedRuleSetId, {
		eventName: `${event} (client)`,
	});
};

const deleteSuccessHandler = (
	state: State,
	deletedRuleSetId: string,
	analyticsEvent: UIAnalyticsEvent, // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Observable<any> => {
	fireDeleteAnalytics(deletedRuleSetId, analyticsEvent);
	// @ts-expect-error - TS2769 - No overload matches this call.
	return Observable.of(setShowDeleteDialogAction(null), deleteSuccessAction(deletedRuleSetId));
};

const createDeleteFailedAddFlagAction = (): AddFlagAction => {
	const flagDescriptor: Flag = {
		id: generateNanoId(),
		title: messages.deleteFailedTitle,
		description: ofI18n(messages.deleteFailedDescription),
		type: ERROR_TYPE,
	};
	return addFlagAction(flagDescriptor);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const deleteFailureHandler = (deletedRuleSetId: string): Observable<any> =>
	Observable.of(
		createDeleteFailedAddFlagAction(),
		// @ts-expect-error - TS2769 - No overload matches this call.
		setShowDeleteDialogAction(null),
		deleteFailureAction(deletedRuleSetId),
	);

// eslint-disable-next-line jira/import/no-anonymous-default-export
export default (action$: ActionsObservable<DeleteRuleAction>, store: MiddlewareAPI<State>) =>
	action$.ofType(DELETE_RULE).switchMap((action) => {
		const state = store.getState();
		const { ruleSetId } = action.payload;
		return deleteRule(state, parseInt(ruleSetId, 10))
			.mergeMap(() => deleteSuccessHandler(state, ruleSetId, action.meta.analyticsEvent))
			.catch((error: Error) => {
				fireErrorAnalytics({
					meta: {
						id: 'deleteRule',
						packageName: 'jiraServicedeskAutomationSummary',
					},
					error,
					sendToPrivacyUnsafeSplunk: true,
				});
				return deleteFailureHandler(ruleSetId);
			});
	});
